/**
 * Shared placement logic for floating Execute|Peek bars. Used by both the
 * cursor-statement bar and the selection bar so their visual behavior is
 * identical — same above/below preference, same collision avoidance, and
 * same view-zone fallback when neither direction has room.
 *
 * Placement decision (re-run whenever `range` / `editorContent` changes —
 * NOT on scroll, so modes don't flicker during scrolling):
 *
 * Checked in order — first match wins. ABOVE is strongly preferred: any
 * reason to place above (blank neighbor OR pixel room) beats any below
 * option, so a single blank line above with two blank lines below still
 * places the bar ABOVE.
 *
 *   1. **Above (blank neighbor)** — the line IMMEDIATELY above the range
 *                    is blank. The glued button visually occupies the
 *                    blank line's area, with a small acceptable overlap
 *                    into the line above it. No view zone is needed — the
 *                    empty line *is* the space.
 *   2. **Above (pixel room)**   — the room between the range's first line
 *                    and the nearest non-blank line above is
 *                    ≥ `buttonHeightWithGap`.
 *   3. **Below (blank neighbor)** — same idea as (1), opposite direction.
 *   4. **Below (pixel room)**   — below has enough room. Anchored to the
 *                    range's END line so multi-line ranges don't have the
 *                    bar floating over their own later lines.
 *   5. **Below + view zone** — last resort when BOTH immediate neighbors
 *                    contain content. Inserts a Monaco view zone after the
 *                    range's end line (SPACER_ZONE_DENSE_NEIGHBOR px) to
 *                    carve out space without overlapping real content.
 *
 * The spacer is intentionally smaller than the bar itself, so the bar
 * visually overlaps the next row by a few pixels rather than floating in
 * isolated whitespace — it reads as "attached to this statement" even in
 * the fallback case.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { EditorSelectionRange } from '@/types/aiWorkspace';
import { findNextNonBlankLine, findPreviousNonBlankLine } from '../utils/statementAtCursor';
import { createBoundedMicrotaskScheduler } from './boundedMicrotaskScheduler';

export type BarPosition = { top: number; left: number; mode: 'above' | 'below' };

interface UseSmartBarPlacementOptions {
  editorRef: RefObject<SqlMonacoHandle | null>;
  editorContent: string;
  /** Source range to anchor the bar to. Null = hide + clear any active spacer. */
  range: EditorSelectionRange | null;
  /** Pass `false` to force-hide (e.g., query running, no executable action). */
  enabled: boolean;
  /** Offset from anchor top when placing ABOVE (button height + gap). */
  buttonHeightWithGap: number;
  /** Gap between anchor bottom and bar top when placing BELOW. */
  belowGap: number;
}

// Height of the view zone used when BOTH immediate neighbors are dense.
// Intentionally smaller than the bar so it overlaps the zoned line by a
// few pixels and reads as "attached" rather than floating in whitespace.
const SPACER_ZONE_DENSE_NEIGHBOR = 19;

// Cap on how many placement passes may chain back-to-back inside one
// microtask drain before the scheduler forces a macrotask boundary. Eight is
// far more than any legitimate settle needs (a scroll burst coalesces into
// ONE pass; a zone insert costs at most one follow-up), and small enough that
// a feedback loop reads as a stutter rather than a frozen window.
const MAX_CHAINED_PLACEMENT_RUNS = 8;

type PlacementMode = 'above' | 'below';

export function useSmartBarPlacement({
  editorRef,
  editorContent,
  range,
  enabled,
  buttonHeightWithGap,
  belowGap,
}: UseSmartBarPlacementOptions): BarPosition | null {
  const [placement, setPlacement] = useState<{ mode: PlacementMode } | null>(null);
  const [position, setPosition] = useState<BarPosition | null>(null);

  const spacerZoneIdRef = useRef<string | null>(null);
  const spacerZoneLineRef = useRef<number | null>(null);
  // Height of the currently-active zone, in pixels. Tracked separately so we
  // can DISCOUNT its contribution from pixel-based room calculations — Monaco
  // reports zoned line positions inclusive of the zone, which would otherwise
  // let a stale zone (or our own zone on the current statement) tip a
  // placement decision to the wrong side.
  const spacerZoneHeightRef = useRef(0);
  const spacerDomRef = useRef<HTMLDivElement | null>(null);

  // Above-mode rescue zone: inserted at `afterLineNumber = startLineNumber - 1`
  // when `tryAbove` would place the bar at `top < 0` — i.e. the anchor is too
  // close to the top of the viewport (typically a selection starting at line 1
  // with the user scrolled to the top while the selection's end is off-screen
  // below). Managed independently of the below-mode spacer; both can coexist
  // briefly when rescue flips a below-placement to above.
  const aboveZoneIdRef = useRef<string | null>(null);
  const aboveZoneAnchorLineRef = useRef<number | null>(null);
  const aboveZoneHeightRef = useRef(0);
  const aboveZoneDomRef = useRef<HTMLDivElement | null>(null);

  // One placement-loop report per hook instance (see the scroll/layout
  // subscription below) — a broken chain re-breaks on every macrotask while
  // the loop lasts, and telemetry needs the fact, not the repetition.
  const loopReportedRef = useRef(false);

  const clearSpacer = useCallback(() => {
    const editor = editorRef.current;
    if (editor && spacerZoneIdRef.current !== null) {
      editor.removeViewZone(spacerZoneIdRef.current);
    }
    spacerZoneIdRef.current = null;
    spacerZoneLineRef.current = null;
    spacerZoneHeightRef.current = 0;
  }, [editorRef]);

  const clearAboveZone = useCallback(() => {
    const editor = editorRef.current;
    if (editor && aboveZoneIdRef.current !== null) {
      editor.removeViewZone(aboveZoneIdRef.current);
    }
    aboveZoneIdRef.current = null;
    aboveZoneAnchorLineRef.current = null;
    aboveZoneHeightRef.current = 0;
  }, [editorRef]);

  // Decide placement + manage spacer lifecycle.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !range || !enabled) {
      clearSpacer();
      setPlacement(null);
      return;
    }

    // `startPos`/`endPos` come from Monaco's `getScrolledVisiblePosition`.
    // In Monaco 0.44 it returns coordinates for ANY valid position (off-screen
    // lines simply get a top outside the viewport) and only returns `null`
    // when the editor has no model/view; the handle also returns `null` when
    // the editor is gone. We still treat an absent position as "no pixel
    // info" — fall back to the editorContent-derived heuristics (blank
    // neighbor checks) and DON'T abort the whole decision on a single null —
    // so the bar stays alive during teardown/hydration edges.
    const startPos = editor.getPixelPositionForPosition(
      range.startLineNumber,
      range.startColumn,
    );
    const endPos = editor.getPixelPositionForPosition(
      range.endLineNumber,
      range.endColumn,
    );
    // If NEITHER anchor is rendered, there's nothing we can usefully place —
    // treat that as "don't know, don't show." This differs from the old
    // behavior which hid the bar on just startPos being null.
    if (!startPos && !endPos) {
      clearSpacer();
      setPlacement(null);
      return;
    }

    // Snapshot the zone we currently own — used below to discount its effect
    // on Monaco's reported pixel positions so the placement decision
    // reflects the TRUE (unzoned) layout.
    const activeZoneLine = spacerZoneLineRef.current;
    const activeZoneHeight = spacerZoneHeightRef.current;

    const lines = editorContent.split('\n');
    const immediatePrev =
      range.startLineNumber >= 2 ? lines[range.startLineNumber - 2] : undefined;
    const immediatePrevIsBlank =
      range.startLineNumber >= 2 &&
      (immediatePrev === undefined || immediatePrev.trim() === '');
    const immediateNext = lines[range.endLineNumber]; // 0-indexed => line endLineNumber + 1
    const immediateNextIsBlank =
      immediateNext === undefined || immediateNext.trim() === '';

    // (1) Blank immediate above wins over ANY below option. The glued
    // button fits the blank line's area cleanly; placing above keeps
    // focus near the statement and avoids pushing follow-on content down.
    if (immediatePrevIsBlank) {
      clearSpacer();
      setPlacement({ mode: 'above' });
      return;
    }

    // (2) Pixel room above — distance from the statement's first line top
    // to the nearest non-blank line's bottom above. If our active zone
    // sits BETWEEN that previous line and the current statement's start,
    // its height is artificially inflating the gap — subtract it so we
    // don't mistakenly pick "above" and then clear the zone (leaving the
    // bar hovering over the previous statement).
    //
    // Only evaluated when startPos is actually rendered — if the start is
    // off-screen (null), we can't measure pixel room up there, and above
    // mode isn't useful anyway (we'd try to anchor the bar above an
    // invisible line). Fall through to the below options.
    if (startPos) {
      let availableAbove = startPos.top;
      const prevLine = findPreviousNonBlankLine(editorContent, range.startLineNumber);
      if (prevLine !== null) {
        const prevPos = editor.getPixelPositionForPosition(prevLine, 1);
        if (prevPos) {
          availableAbove = Math.max(0, startPos.top - (prevPos.top + prevPos.height));
        }
      }
      if (activeZoneLine !== null && activeZoneLine < range.startLineNumber) {
        availableAbove = Math.max(0, availableAbove - activeZoneHeight);
      }
      // Same discount for the ABOVE-mode rescue zone (see `updatePosition`'s
      // invariance rule): it is inserted at `anchorLine - 1`, so whenever it
      // is anchored at or above our start line it sits inside the very gap we
      // just measured and inflates it by its own height. Without this, a zone
      // we added because there was NO room could report that there now IS
      // room, and the decision would depend on our own output.
      const aboveZoneAnchor = aboveZoneAnchorLineRef.current;
      if (
        aboveZoneAnchor !== null &&
        aboveZoneAnchor <= range.startLineNumber &&
        (prevLine === null || aboveZoneAnchor - 1 >= prevLine)
      ) {
        availableAbove = Math.max(0, availableAbove - aboveZoneHeightRef.current);
      }
      if (availableAbove >= buttonHeightWithGap) {
        clearSpacer();
        setPlacement({ mode: 'above' });
        return;
      }
    }

    // (3) Blank immediate below — symmetric to (1).
    if (immediateNextIsBlank) {
      clearSpacer();
      setPlacement({ mode: 'below' });
      return;
    }

    // (4) Pixel room below — distance from the statement's last line
    // bottom to the nearest non-blank line's top below. If our active
    // zone is anchored at this same endLine (a zone we added on a
    // previous pass for the same statement), it's sitting between
    // endBottom and nextLine.top, inflating the gap — subtract it so the
    // decision stays stable across re-renders instead of oscillating
    // (clear → re-add → clear → …).
    //
    // If endPos is null (end line off-screen) we skip pixel-room and go
    // straight to the view-zone fallback — we can't measure room without a
    // pixel anchor, and inserting a zone at the end line is always safe.
    if (endPos) {
      const endBottom = endPos.top + endPos.height;
      let availableBelow = Number.POSITIVE_INFINITY;
      const nextLine = findNextNonBlankLine(editorContent, range.endLineNumber);
      if (nextLine !== null) {
        const nextPos = editor.getPixelPositionForPosition(nextLine, 1);
        if (nextPos) {
          availableBelow = Math.max(0, nextPos.top - endBottom);
        }
      }
      if (activeZoneLine === range.endLineNumber) {
        availableBelow = Math.max(0, availableBelow - activeZoneHeight);
      }
      // A rescue zone left over from a PREVIOUS range can sit between this
      // range's end line and the next non-blank line (it lives at
      // `anchorLine - 1`, so anchors in (endLine, nextLine] land in that
      // gap). Discount it like the spacer above — symmetric with step (2).
      const staleAboveZoneAnchor = aboveZoneAnchorLineRef.current;
      if (
        staleAboveZoneAnchor !== null &&
        staleAboveZoneAnchor > range.endLineNumber &&
        (nextLine === null || staleAboveZoneAnchor <= nextLine)
      ) {
        availableBelow = Math.max(0, availableBelow - aboveZoneHeightRef.current);
      }
      if (availableBelow >= buttonHeightWithGap) {
        clearSpacer();
        setPlacement({ mode: 'below' });
        return;
      }
    }

    // (5) Both neighbors are dense — the only case where we need to carve
    // vertical space ourselves. Insert a below-zone at the range's end
    // line. Idempotent per endLine so the zone doesn't flicker while the
    // user works inside the same statement.
    if (spacerZoneLineRef.current !== range.endLineNumber) {
      clearSpacer();
      if (!spacerDomRef.current) {
        spacerDomRef.current = document.createElement('div');
      }
      const zoneId = editor.addViewZone(
        range.endLineNumber,
        SPACER_ZONE_DENSE_NEIGHBOR,
        spacerDomRef.current,
      );
      if (zoneId) {
        spacerZoneIdRef.current = zoneId;
        spacerZoneLineRef.current = range.endLineNumber;
        spacerZoneHeightRef.current = SPACER_ZONE_DENSE_NEIGHBOR;
      }
    }
    setPlacement({ mode: 'below' });
  }, [editorRef, range, editorContent, enabled, buttonHeightWithGap, clearSpacer]);

  // Clean up any outstanding zone on unmount.
  useEffect(() => clearSpacer, [clearSpacer]);

  // Resolve pixel coordinates from the chosen mode + current scroll state.
  const recomputePosition = useCallback((): BarPosition | null => {
    const editor = editorRef.current;
    if (!editor || !range || !placement) return null;

    const layoutHeight = editor.getLayoutHeight();

    // INVARIANCE RULE, visibility half (see `updatePosition`): every anchor
    // below our own rescue zone is reported `aboveZoneHeight` px lower than
    // it really is. The viewport-overlap tests below therefore run on the
    // UNZONED offset, otherwise a zone we inserted can push an anchor that
    // straddles the viewport edge from "visible" to "not visible" — and the
    // next pass, seeing no anchor, removes the zone, which makes it visible
    // again. That is the same add/remove feedback as the `top < 0` case,
    // just through the other test; it showed up as a capped thrash in a
    // hidden (0px-high) background tab. The RETURNED coordinates stay zoned,
    // because that is where the pixels really are.
    const rescueShiftFor = (lineNumber: number): number =>
      aboveZoneAnchorLineRef.current !== null &&
      aboveZoneAnchorLineRef.current <= lineNumber
        ? aboveZoneHeightRef.current
        : 0;
    const overlapsViewport = (top: number, height: number): boolean =>
      layoutHeight === null || (top + height >= 0 && top <= layoutHeight);

    // Above-mode anchor: returns a BarPosition iff the START line is
    // currently rendered AND its (unzoned) pixel rect overlaps the viewport.
    const tryAbove = (): BarPosition | null => {
      const startPos = editor.getPixelPositionForPosition(
        range.startLineNumber,
        range.startColumn,
      );
      if (!startPos) return null;
      const unzonedTop = startPos.top - rescueShiftFor(range.startLineNumber);
      if (!overlapsViewport(unzonedTop, startPos.height)) return null;
      return {
        top: startPos.top - buttonHeightWithGap,
        left: Math.max(0, startPos.left),
        mode: 'above',
      };
    };

    // Below-mode anchor: returns a BarPosition iff the END line is currently
    // rendered AND its pixel rect overlaps the viewport. The horizontal
    // anchor mirrors today's logic: prefer `startPos.left`, fall back to
    // column 1 of the end line, then `endPos.left` — "leftmost edge of the
    // selection on a currently-rendered line."
    const tryBelow = (): BarPosition | null => {
      const endPos = editor.getPixelPositionForPosition(
        range.endLineNumber,
        range.endColumn,
      );
      if (!endPos) return null;
      const endBottom = endPos.top + endPos.height;
      const unzonedTop = endPos.top - rescueShiftFor(range.endLineNumber);
      if (!overlapsViewport(unzonedTop, endPos.height)) return null;
      const startPos = editor.getPixelPositionForPosition(
        range.startLineNumber,
        range.startColumn,
      );
      const startOfEndLine = startPos
        ? null
        : editor.getPixelPositionForPosition(range.endLineNumber, 1);
      const leftAnchor = startPos?.left ?? startOfEndLine?.left ?? endPos.left;
      return {
        top: endBottom + belowGap,
        left: Math.max(0, leftAnchor),
        mode: 'below',
      };
    };

    // Viewport rescue: try the placement the 5-tier algorithm picked first.
    // If its anchor isn't visible (scrolled off-screen) but the opposite
    // end IS visible, return the other mode's position for this frame.
    // The stored `placement` is not mutated — on back-scroll, when the
    // preferred anchor becomes visible again, this function naturally
    // returns it. No sticky state, no hysteresis.
    return placement.mode === 'above'
      ? (tryAbove() ?? tryBelow())
      : (tryBelow() ?? tryAbove());
  }, [editorRef, range, placement, buttonHeightWithGap, belowGap]);

  // Update `position` from the current scroll state. Wraps `recomputePosition`
  // with the above-mode rescue-zone lifecycle: when the raw result would clip
  // the bar above the viewport (`top < 0`), insert/resize a view zone before
  // the anchor line to push it down and re-compute. When the clip resolves
  // (e.g. user scrolls such that the preferred below-mode succeeds again),
  // any lingering above-zone is removed so no stale space remains.
  //
  // INVARIANCE RULE (Jeremy's freeze report, 2026-09-08). The "does the bar
  // clip above the viewport" question is answered against the UNZONED anchor:
  // `raw.top` minus the height of the rescue zone we currently own, whenever
  // that zone sits above the anchor. Monaco reports the ZONED layout, and our
  // zone shifts the anchor down by exactly `buttonHeightWithGap` — so for
  // every anchor within one bar-height of the viewport top the two layouts
  // disagree: zoned says "no clip" (remove the zone), unzoned says "clip"
  // (add it). Each toggle changes Monaco's scrollHeight, which SYNCHRONOUSLY
  // fires a scroll event (`viewLayout.changeWhitespace` →
  // `onHeightMaybeChanged` → `setScrollDimensions` → `onDidScroll`), which
  // re-runs this function — an unbounded add/remove chain that hard-froze the
  // renderer (`rm#b1, add#b2, rm#b2, add#b3, …` at scrollTop = 7px).
  // Discounting our own zone makes the decision a function of the document
  // and the scroll offset alone, so it can never depend on its own output.
  const updatePosition = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !range) {
      if (aboveZoneIdRef.current !== null) clearAboveZone();
      setPosition(null);
      return;
    }

    const raw = recomputePosition();
    if (!raw) {
      if (aboveZoneIdRef.current !== null) clearAboveZone();
      setPosition(null);
      return;
    }

    // Strip our own rescue zone's contribution back out of the measurement
    // before deciding (see INVARIANCE RULE above). The zone is inserted at
    // `anchorLine - 1`, so it only pushes the anchor down while it is
    // anchored at or above the current start line.
    const activeAboveZoneAnchor = aboveZoneAnchorLineRef.current;
    const unzonedTop =
      raw.mode === 'above' &&
      activeAboveZoneAnchor !== null &&
      activeAboveZoneAnchor <= range.startLineNumber
        ? raw.top - aboveZoneHeightRef.current
        : raw.top;

    if (raw.mode === 'above' && unzonedTop < 0) {
      const neededHeight = buttonHeightWithGap;
      const anchorLine = range.startLineNumber;
      // (Re)insert the zone only when its key attributes change — idempotent
      // across scroll frames while in the same clipped state.
      if (
        aboveZoneIdRef.current === null ||
        aboveZoneAnchorLineRef.current !== anchorLine ||
        aboveZoneHeightRef.current !== neededHeight
      ) {
        clearAboveZone();
        if (!aboveZoneDomRef.current) {
          aboveZoneDomRef.current = document.createElement('div');
        }
        const zoneId = editor.addViewZone(
          anchorLine - 1, // afterLineNumber: 0 for line 1 (before the first line)
          neededHeight,
          aboveZoneDomRef.current,
        );
        if (zoneId) {
          aboveZoneIdRef.current = zoneId;
          aboveZoneAnchorLineRef.current = anchorLine;
          aboveZoneHeightRef.current = neededHeight;
        }
      }
      // After the zone is added, Monaco shifts the anchor line down by
      // `neededHeight`, so the re-compute yields a non-negative top.
      setPosition(recomputePosition());
      return;
    }

    // Clearing a leftover above-zone here is non-trivial: `raw` was just
    // computed against Monaco's CURRENT layout, which includes the zone's
    // height. If we naively `setPosition(raw)` and then drop the zone,
    // every line snaps back up by `aboveZoneHeightRef.current` px but the
    // bar keeps its zone-shifted top — visible as a one-line gap below the
    // anchor. Monaco DOES fire a scroll event for the removal (dropping a
    // zone changes scrollHeight), but that only reaches us a deferred pass
    // later, i.e. after a frame with the bar in the wrong place. Re-running
    // `recomputePosition` AFTER the clear gives us coordinates from the
    // un-shifted layout in this same pass.
    if (aboveZoneIdRef.current !== null) {
      clearAboveZone();
      setPosition(recomputePosition());
      return;
    }
    setPosition(raw);
  }, [editorRef, range, recomputePosition, clearAboveZone, buttonHeightWithGap]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Re-run `updatePosition` whenever the editor changes externally — both
  // scroll AND layout events trigger a refresh.
  //
  // Why layout matters: workspace tabs keep their Monaco editors mounted but
  // hidden via CSS when another tab is active. Hiding → container size 0 →
  // Monaco's `getScrolledVisiblePosition` returns coordinates relative to a
  // 0-height viewport → visibility check fails → `position = null`. When the
  // tab becomes visible again, no scroll event fires (scroll offset didn't
  // change), so a scroll-only subscription would leave `position = null`
  // indefinitely — the bar would stay hidden even though the selection + view
  // zone are still there. Reacting to `onDidLayoutChange` covers tab
  // show/hide, window resize, and side-panel toggles.
  //
  // Deliberately not gated on `position` being truthy — previously the
  // subscription unmounted when the bar was hidden, which defeated the
  // exact scenario we're trying to fix (hidden → need to come back).
  //
  // Deferred, never synchronous: `updatePosition` mutates view zones, and
  // Monaco emits scroll/layout events synchronously MID-render-pass — after
  // it has already snapshotted the whitespace viewport data (its own
  // `_actualRender` comment admits "Rendering of viewLines might cause
  // scroll events to occur"). Removing a zone inside that window leaves the
  // snapshot referencing a zone id that no longer exists in
  // `ViewZones._zones`, crashing render with "Cannot read properties of
  // undefined (reading 'isInHiddenArea')" (upstream: vscode#196669; seen in
  // v1.4.12 production crash reports during rapid tab switching). A
  // microtask lands after the render pass but before paint, so the bar
  // still re-anchors within the same frame.
  //
  // The microtask is BOUNDED (`createBoundedMicrotaskScheduler`): view-zone
  // mutations here feed straight back into the scroll events that trigger
  // them, and microtasks are drained to exhaustion before the event loop gets
  // another turn — so a placement pass that keeps re-triggering itself
  // freezes the renderer outright rather than merely spinning. The invariance
  // rule on `updatePosition` removes the loop we know about; the cap is what
  // keeps the NEXT one a stutter.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !range) return;
    const scheduler = createBoundedMicrotaskScheduler(updatePosition, {
      maxChainedRuns: MAX_CHAINED_PLACEMENT_RUNS,
      onChainBroken: (chainedRuns) => {
        // Once per hook instance: a live loop breaks on every macrotask, and
        // neither the console nor the dashboard should be flooded by it.
        if (loopReportedRef.current) return;
        loopReportedRef.current = true;
        console.warn(
          `[useSmartBarPlacement] inline bar placement re-entered ${chainedRuns} times in one microtask chain — deferring to a macrotask`,
        );
        window.electronAPI?.analytics
          ?.track?.('ui:inline_bar_placement_loop_broken', { chainedRuns })
          ?.catch(() => {});
      },
    });
    const unsubScroll = editor.onDidScrollChange(scheduler.schedule);
    const unsubLayout = editor.onDidLayoutChange(scheduler.schedule);
    return () => {
      scheduler.dispose();
      unsubScroll();
      unsubLayout();
    };
  }, [editorRef, range, updatePosition]);

  // Cleanup any outstanding above-zone on unmount. The below-zone has its own
  // cleanup effect (see `clearSpacer` above).
  useEffect(() => clearAboveZone, [clearAboveZone]);

  return position;
}
